import {
  Document,
  Page,
  Text,
  View,
  Image,
  Link,
  StyleSheet,
  Font,
} from '@react-pdf/renderer'
import type { CV, DateFormat, CVLanguage } from '../../types/cv'
import { cleanPalettes, type CleanColors } from '../../utils/colorPalettes'
import { getCVLabels, getLevelLabel } from '../../utils/cvTranslations'
import { EmailIcon, PhoneIcon, LocationIcon, BirthDateIcon } from './ContactIcons'

function getColors(paletteId?: string): CleanColors {
  return cleanPalettes[paletteId || 'blue'] || cleanPalettes.blue
}

// Register Source Sans Pro font
Font.register({
  family: 'Source Sans Pro',
  fonts: [
    {
      src: 'https://fonts.gstatic.com/s/sourcesanspro/v22/6xK3dSBYKcSV-LCoeQqfX1RYOo3aPw.ttf',
      fontWeight: 400,
    },
    {
      src: 'https://fonts.gstatic.com/s/sourcesanspro/v22/6xKydSBYKcSV-LCoeQqfX1RYOo3i54rAkA.ttf',
      fontWeight: 600,
    },
    {
      src: 'https://fonts.gstatic.com/s/sourcesanspro/v22/6xKydSBYKcSV-LCoeQqfX1RYOo3ig4vAkA.ttf',
      fontWeight: 700,
    },
  ],
})


const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontFamily: 'Source Sans Pro',
    fontSize: 10,
    color: '#0f172a',
    lineHeight: 1.5,
  },
  header: {
    marginBottom: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    paddingBottom: 15,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 15,
  },
  headerPhoto: {
    width: 80,
    height: 80,
    borderRadius: 8,
    objectFit: 'cover',
  },
  headerInfo: {
    flex: 1,
  },
  name: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 4,
  },
  title: {
    fontSize: 14,
    color: '#1e40af',
    fontWeight: 600,
    marginBottom: 8,
  },
  contactRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    fontSize: 9,
    color: '#64748b',
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    textDecoration: 'none',
  },
  contactIcon: {
    marginRight: 3,
  },
  linksRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 10,
    marginTop: 8,
  },
  link: {
    fontSize: 9,
    color: '#1e40af',
  },
  section: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 8,
    color: '#0f172a',
  },
  paragraph: {
    fontSize: 10,
    color: '#334155',
  },
  entryContainer: {
    marginBottom: 12,
  },
  entryHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 2,
  },
  entryTitle: {
    fontSize: 11,
    fontWeight: 600,
    color: '#0f172a',
  },
  entrySubtitle: {
    fontSize: 10,
    color: '#1e40af',
  },
  entryDate: {
    fontSize: 9,
    color: '#64748b',
    textAlign: 'right',
  },
  entryLocation: {
    fontSize: 9,
    color: '#64748b',
    textAlign: 'right',
  },
  entryDescription: {
    fontSize: 9,
    color: '#334155',
    marginTop: 4,
  },
  skillsRow: {
    marginBottom: 4,
  },
  skillLabel: {
    fontWeight: 600,
    color: '#334155',
  },
  skillValue: {
    color: '#64748b',
  },
  languagesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
  },
  languageItem: {
    fontSize: 10,
  },
  languageName: {
    fontWeight: 600,
    color: '#334155',
  },
  languageLevel: {
    color: '#64748b',
  },
})

const localeMap: Record<CVLanguage, string> = {
  es: 'es-ES',
  en: 'en-US',
  fr: 'fr-FR',
  pt: 'pt-PT',
  de: 'de-DE',
}

function formatDate(
  dateStr: string | undefined,
  format: DateFormat = 'month-year',
  language: CVLanguage = 'en',
  presentLabel: string = 'Present'
): string {
  if (!dateStr) return ''

  const str = dateStr.trim().toLowerCase()
  const locale = localeMap[language] || 'en-US'

  // Handle "present", "actual", "current"
  if (str.includes('present') || str.includes('actual') || str.includes('current')) {
    return presentLabel
  }

  const yearOnly = format === 'year-only'

  // Try YYYY-MM format (e.g., "2024-01")
  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    const [year, month] = dateStr.split('-')
    if (yearOnly) return year
    const date = new Date(parseInt(year), parseInt(month) - 1)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(locale, { month: 'short', year: 'numeric' })
    }
  }

  // Try YYYY-MM-DD format (e.g., "2024-01-15")
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    if (yearOnly) return dateStr.split('-')[0]
    const date = new Date(dateStr)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(locale, { month: 'short', year: 'numeric' })
    }
  }

  // Try just year (e.g., "2024")
  if (/^\d{4}$/.test(dateStr)) {
    return dateStr
  }

  // Try MM/YYYY or MM-YYYY format
  const mmYYYYMatch = dateStr.match(/^(\d{1,2})[/-](\d{4})$/)
  if (mmYYYYMatch) {
    if (yearOnly) return mmYYYYMatch[2]
    const date = new Date(parseInt(mmYYYYMatch[2]), parseInt(mmYYYYMatch[1]) - 1)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(locale, { month: 'short', year: 'numeric' })
    }
  }

  // Extract year from string if present (e.g., "Enero 2024" -> "2024")
  const yearMatch = dateStr.match(/\b(19|20)\d{2}\b/)
  if (yearMatch) {
    return yearMatch[0]
  }

  // If nothing works, return empty string instead of "Invalid Date"
  return ''
}

function formatDateRange(
  start: string | undefined,
  end: string | undefined,
  current: boolean | undefined,
  format: DateFormat = 'month-year',
  language: CVLanguage = 'en',
  presentLabel: string = 'Present'
): string {
  const startStr = formatDate(start, format, language, presentLabel)
  const endStr = current ? presentLabel : formatDate(end, format, language, presentLabel)
  if (!startStr && !endStr) return ''
  if (!startStr) return endStr
  if (!endStr) return startStr
  return `${startStr} - ${endStr}`
}


interface CleanTemplateProps {
  cv: CV
  colorPalette?: string
}


export function CleanTemplate({ cv, colorPalette }: CleanTemplateProps) {
  const colors = getColors(colorPalette)
  const labels = getCVLabels(cv.settings?.language, cv.settings?.customLabels)
  const showIcons = cv.settings?.showContactIcons ?? true
  const hasPersonalInfo =
    cv.personalInfo?.fullName ||
    cv.personalInfo?.title ||
    cv.personalInfo?.email ||
    cv.personalInfo?.phone ||
    cv.personalInfo?.location

  const hasExperience = cv.experience && cv.experience.length > 0
  const hasEducation = cv.education && cv.education.length > 0
  const hasSkills =
    (cv.skills?.technical && cv.skills.technical.length > 0) ||
    (cv.skills?.soft && cv.skills.soft.length > 0) ||
    (cv.skills?.tools && cv.skills.tools.length > 0)
  const hasLanguages =
    cv.languages && cv.languages.some((lang) => lang.name)

  return (
    <Document
      title={`CV - ${cv.personalInfo?.fullName || 'Curriculum Vitae'}`}
      author={cv.personalInfo?.fullName || ''}
      subject={cv.personalInfo?.title || 'Curriculum Vitae'}
      creator="CVtoday"
      keywords={`CV, resume, ${cv.personalInfo?.fullName || ''}, ${cv.personalInfo?.title || ''}`}
    >
      <Page size="A4" style={styles.page}>
        {/* Header */}
        {hasPersonalInfo && (
          <View style={styles.header}>
            <View style={styles.headerContent}>
              {cv.personalInfo?.photo && (
                <Image src={cv.personalInfo.photo} style={styles.headerPhoto} />
              )}
              <View style={styles.headerInfo}>
                {cv.personalInfo?.fullName && (
                  <Text style={styles.name}>{cv.personalInfo.fullName}</Text>
                )}
                {cv.personalInfo?.title && (
                  <Text style={[styles.title, { color: colors.primary }]}>{cv.personalInfo.title}</Text>
                )}
                <View style={styles.contactRow}>
                  {cv.personalInfo?.email && (
                    <Link src={`mailto:${cv.personalInfo.email}`} style={styles.contactItem}>
                      {showIcons && <EmailIcon size={10} color="#64748b" style={{ marginRight: 4 }} />}
                      <Text>{cv.personalInfo.email}</Text>
                    </Link>
                  )}
                  {cv.personalInfo?.phone && (
                    <Link src={`tel:${cv.personalInfo.phone}`} style={styles.contactItem}>
                      {showIcons && <PhoneIcon size={10} color="#64748b" style={{ marginRight: 4 }} />}
                      <Text>{cv.personalInfo.phone}</Text>
                    </Link>
                  )}
                  {cv.personalInfo?.location && (
                    <View style={styles.contactItem}>
                      {showIcons && <LocationIcon size={10} color="#64748b" style={{ marginRight: 4 }} />}
                      <Text>{cv.personalInfo.location}</Text>
                    </View>
                  )}
                  {cv.personalInfo?.birthDate && (
                    <View style={styles.contactItem}>
                      {showIcons && <BirthDateIcon size={10} color="#64748b" style={{ marginRight: 4 }} />}
                      <Text>{cv.personalInfo.birthDate}</Text>
                    </View>
                  )}
                </View>
                {cv.personalInfo?.links && cv.personalInfo.links.length > 0 && (
                  <View style={styles.linksRow}>
                    {cv.personalInfo.links.map(
                      (link) =>
                        link.url ? (
                          <Link key={link.id} src={link.url} style={[styles.link, { color: colors.primary }]}>
                            {link.label || link.url}
                          </Link>
                        ) : null
                    )}
                  </View>
                )}
              </View>
            </View>
          </View>
        )}

        {/* Summary */}
        {cv.summary && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle} minPresenceAhead={20}>{labels.summary}</Text>
            <Text style={styles.paragraph}>{cv.summary}</Text>
          </View>
        )}

        {/* Experience */}
        {hasExperience && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle} minPresenceAhead={40}>{labels.experience}</Text>
            {cv.experience?.map((exp) => (
              <View key={exp.id} style={styles.entryContainer}>
                <View style={styles.entryHeader}>
                  <View>
                    <Text style={styles.entryTitle}>
                      {exp.position || 'Puesto'}
                    </Text>
                    {exp.company && (
                      <Text style={[styles.entrySubtitle, { color: colors.primary }]}>{exp.company}</Text>
                    )}
                  </View>
                  <View>
                    <Text style={styles.entryDate}>
                      {formatDateRange(exp.startDate, exp.endDate, exp.current, cv.settings?.dateFormat, cv.settings?.language, labels.present)}
                    </Text>
                    {exp.location && (
                      <Text style={styles.entryLocation}>{exp.location}</Text>
                    )}
                  </View>
                </View>
                {exp.description && (
                  <Text style={styles.entryDescription}>{exp.description}</Text>
                )}
              </View>
            ))}
          </View>
        )}

        {/* Education */}
        {hasEducation && (
          <View style={styles.section}>
            <Text style={styles.sectionTitle} minPresenceAhead={40}>{labels.education}</Text>
            {cv.education?.map((edu) => (
              <View key={edu.id} style={styles.entryContainer}>
                <View style={styles.entryHeader}>
                  <View>
                    <Text style={styles.entryTitle}>
                      {edu.degree}
                      {edu.field && ` en ${edu.field}`}
                    </Text>
                    {edu.institution && (
                      <Text style={[styles.entrySubtitle, { color: colors.primary }]}>{edu.institution}</Text>
                    )}
                  </View>
                  <View>
                    <Text style={styles.entryDate}>
                      {formatDateRange(edu.startDate, edu.endDate, edu.current, cv.settings?.dateFormat, cv.settings?.language, labels.present)}
                    </Text>
                    {edu.location && (
                      <Text style={styles.entryLocation}>{edu.location}</Text>
                    )}
                  </View>
                </View>
                {edu.gpa && (
                  <Text style={styles.entryDescription}>{labels.gpa}: {edu.gpa}</Text>
                )}
              </View>
            ))}
          </View>
        )}

        {/* Skills */}
        {hasSkills && (
          <View style={styles.section} wrap={false}>
            <Text style={styles.sectionTitle}>{labels.skills}</Text>
            {cv.skills?.technical && cv.skills.technical.length > 0 && (
              <View style={styles.skillsRow}>
                <Text>
                  <Text style={styles.skillLabel}>{labels.technical}: </Text>
                  <Text style={styles.skillValue}>
                    {cv.skills.technical.join(', ')}
                  </Text>
                </Text>
              </View>
            )}
            {cv.skills?.soft && cv.skills.soft.length > 0 && (
              <View style={styles.skillsRow}>
                <Text>
                  <Text style={styles.skillLabel}>{labels.soft}: </Text>
                  <Text style={styles.skillValue}>
                    {cv.skills.soft.join(', ')}
                  </Text>
                </Text>
              </View>
            )}
            {cv.skills?.tools && cv.skills.tools.length > 0 && (
              <View style={styles.skillsRow}>
                <Text>
                  <Text style={styles.skillLabel}>{labels.tools}: </Text>
                  <Text style={styles.skillValue}>
                    {cv.skills.tools.join(', ')}
                  </Text>
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Languages */}
        {hasLanguages && (
          <View style={styles.section} wrap={false}>
            <Text style={styles.sectionTitle}>{labels.languages}</Text>
            <View style={styles.languagesRow}>
              {cv.languages?.map(
                (lang) =>
                  lang.name ? (
                    <Text key={lang.id} style={styles.languageItem}>
                      <Text style={styles.languageName}>{lang.name}</Text>
                      {lang.level ? (
                        <Text style={styles.languageLevel}>
                          {' '}
                          ({getLevelLabel(lang.level, cv.settings?.language)})
                        </Text>
                      ) : null}
                    </Text>
                  ) : null
              )}
            </View>
          </View>
        )}
      </Page>
    </Document>
  )
}
